<!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
<template>
  <div id="control-compass" :class="{ disabled }" @click="setNorthUp">
    <x-icon :icon="compass" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { camera, setDestination, toggledLayers } from '@/services/map-service'
import { isActive } from '@/services/feature-flag-service'
import { useLayers } from '@/services/layer-management-service/composables/use-layers'
import Camera from '@/lib/camera'
import xIcon from '@/components/shared/x-icon/index.vue'

interface Props {
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
})

const { getUserLayerById } = useLayers()

const compass = ref<string>('compass')

const isLMSActive = computed(() => isActive('mx_layer_management_service'))
const slopeAspectLayer = computed(() => getUserLayerById('slope_aspect'))

// Watch for LMS-managed slope aspect layer
watch(slopeAspectLayer, (userLayer) => {
  // Skip if LMS is not active
  if (!isLMSActive.value) {
    return
  }

  // checks if slope_aspect is active and not disabled
  const isActive = !!(userLayer?.active && !userLayer?.disabled)
  // Add a special compass HUD when slope aspect is on
  compass.value = isActive ? 'compass-slope-aspect' : 'compass'
})

// Watch for legacy toggledLayers (when LMS is OFF)
watch(
  toggledLayers,
  (layers) => {
    // Skip if LMS is active
    if (isLMSActive.value) {
      return
    }

    // Add a special compass HUD when slope aspect is on
    const aspectLayer = layers['slope_aspect$']
    if (aspectLayer?.active) {
      compass.value = 'compass-slope-aspect'
    } else {
      compass.value = 'compass'
    }
  },
  { deep: true, immediate: true }
)

// Watch camera for rotation
watch(camera, (cameraObj) => {
  if (!cameraObj) {
    return
  }
  const { bearing } = cameraObj
  const el = document.getElementById('control-compass')
  if (el) {
    el.style.transform = `rotate(${-bearing}deg)`
  }
})

const setNorthUp = () => {
  if (props.disabled) {
    return
  }

  const cameraObj = camera()
  if (!cameraObj) {
    return
  }

  const { location, pitch } = cameraObj
  const newCamera = new Camera({ location, pitch })
  setDestination(newCamera)
}
</script>

<style lang="scss" scoped>
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_elements.scss';
@use '@/styles/system/_breakpoints.scss';

#control-compass {
  position: relative;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: colors.$BaseBlack;
  cursor: pointer;
  overflow: hidden;

  .x-icon {
    display: flex;
    height: 100%;
    width: 100%;
    align-items: center;
    justify-content: center;
    margin-left: -0.5px;
    pointer-events: none;
  }

  &.disabled::before {
    @include elements.disabledOverlay();
  }

  @include breakpoints.app-prototyping() {
    position: fixed;
    top: 135px;
    right: var(--mobile-controls-offset);
  }
}
</style>
